import { useMemo } from 'react'
import { useDataValue } from 'Simple/Data.js'
import View from './view.js'
import ActionsCell from './ActionsCell/index.js'

let ROW_HEIGHT = 44
let HEADER_HEIGHT = 36
let TABLE_HEIGHT = ROW_HEIGHT * 8 + HEADER_HEIGHT

// Map filter values to data types (1:1 mapping since filters are singular)
let FILTER_TO_TYPE_MAP = {
  payment: 'payment',
  charge: 'charge',
  refund: 'refund',
  correction: 'correction',
  adjustment: 'adjustment',
  transfer: 'transfer',
  general: 'general',
}

function formatDate(value) {
  let date = new Date(value)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Type badge component for the Type column
function TypeBadge({ type, label }) {
  let colors = {
    payment: { bg: '#E8F5E9', text: '#2E7D32' },
    refund: { bg: '#FCE4EC', text: '#C2185B' },
    charge: { bg: '#FFF3E0', text: '#EF6C00' },
    correction: { bg: '#F3E5F5', text: '#7B1FA2' },
    adjustment: { bg: '#FFF3E0', text: '#EF6C00' },
    transfer: { bg: '#E3F2FD', text: '#1976D2' },
    general: { bg: '#F5F5F5', text: '#616161' },
    invoice: { bg: '#E8F5E9', text: '#2E7D32' },
  }

  let style = colors[type] || colors.general

  return (
    <div
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.text,
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      {label}
    </div>
  )
}

export default function Logic(props) {
  let notes = useDataValue({
    context: 'notes',
    viewPath: props.viewPath,
  })

  let selectedFilter = useDataValue({
    context: 'tab',
    path: 'selected.ledger.notes.filter_by',
    viewPath: props.viewPath,
  })

  let columnDefs = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Type',
        initialWidth: 140,
        minWidth: 120,
        cellRenderer: params => (
          <TypeBadge type={params.value} label={params.data.type_label} />
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        initialWidth: 180,
        minWidth: 160,
        valueFormatter: params => formatDate(params.value),
        cellStyle: {
          color: '#52606D',
        },
      },
      {
        field: 'payer',
        headerName: 'Payer',
        initialWidth: 200,
        minWidth: 150,
        cellStyle: {
          color: '#52606D',
        },
      },
      {
        field: 'note',
        headerName: 'Note',
        initialWidth: 400,
        minWidth: 300,
        flex: 1,
        cellStyle: {
          color: '#52606D',
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        initialWidth: 80,
        minWidth: 70,
        cellRenderer: params => (
          <ActionsCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/ActionsCell(${params.data.id})`}
          />
        ),
      },
    ],
    [props.viewPath]
  )

  let filteredData = useMemo(() => {
    // Empty array means "all" - no filtering
    if (!selectedFilter || selectedFilter.length === 0) {
      return notes
    }

    // Non-empty array means specific types selected
    // Convert filter values to data types
    let dataTypes = selectedFilter.map(f => FILTER_TO_TYPE_MAP[f] || f)
    return notes.filter(item => dataTypes.includes(item.type))
  }, [selectedFilter, notes])

  return (
    <View
      {...props}
      columnDefs={columnDefs}
      rowData={filteredData}
      height={TABLE_HEIGHT}
    />
  )
}
